import React, { useState } from 'react';
import { Card, Table as ReactTable } from 'react-bootstrap';
import './Table.scss';
import { Spinner } from '../feedback/Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpToLine } from '@fortawesome/pro-regular-svg-icons';
import { Button } from '@salvapp/ui-components';
import Tracker from '../tracking/tracker';
import { Link } from '../common/scroller';

interface UnknownSizeLoadMoreTableInterface {
  title: string;
  header: React.ReactNode;
  rows: React.ReactNode[];
  fetchData: () => Promise<unknown>;
  maxBatchSize: number;
  borderless?: string;
  className?: string;
  striped?: boolean;
  cardHeaderLeftPane?: React.ReactNode;
  cardHeaderRightPane?: React.ReactNode;
  loading?: boolean;
}

export const UnknownSizeLoadMoreTable = ({
  title,
  header,
  rows,
  maxBatchSize,
  fetchData,
  borderless,
  className = '',
  striped,
  cardHeaderLeftPane,
  cardHeaderRightPane,
  loading = false,
}: UnknownSizeLoadMoreTableInterface): React.JSX.Element => {
  const [fetching, setFetching] = useState<boolean>(false);
  const loadMore = () => {
    setFetching(true);
    fetchData()
      .catch((e: unknown) => {
        Tracker.error(e);
      })
      .finally(() => setFetching(false));
  };

  return (
    <React.Fragment>
      <Card
        id={title}
        body={false}
        className={`${borderless ? 'no-border' : ''} ${className} load-more-table`}
        data-testid='load-more-table-card'
      >
        <Card.Header data-testid='load-more-table-header' className='d-flex'>
          <span className='d-flex'>
            {title && <h4 className='card-header-title'>{title}</h4>}
            {cardHeaderLeftPane}
          </span>
          {cardHeaderRightPane}
        </Card.Header>
        {loading && (
          <Card.Body className='row align-items-center justify-content-center'>
            <Spinner loading />
          </Card.Body>
        )}
        {!loading && rows && rows.length > 0 && (
          <>
            <ReactTable hover responsive size='sm' className='card-table' striped={striped}>
              <thead>{header}</thead>
              <tbody>{rows}</tbody>
            </ReactTable>
            <Card.Footer className={rows && rows.length % maxBatchSize === 0 ? 'load-more-footer' : ''}>
              {fetching ? (
                <div className='row align-items-center justify-content-center'>
                  <Spinner loading />
                </div>
              ) : (
                <div className='row load-more align-items-center justify-content-between'>
                  Showing {rows?.length} alerts
                  <div className='load-more-buttons'>
                    {!rows ||
                      (rows.length % maxBatchSize === 0 && (
                        <Button onClick={loadMore} variant='primary'>
                          Load more
                        </Button>
                      ))}
                    {title && (
                      <Link to={title} smooth duration={400}>
                        <Button variant='outline-secondary'>
                          <FontAwesomeIcon icon={faArrowUpToLine} aria-label='Back to top' />
                          Back to top
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </Card.Footer>
          </>
        )}
        {!loading && (!rows || rows.length === 0) && (
          <Card.Body>
            <div className='small'>Nothing to show here, yet.</div>
          </Card.Body>
        )}
      </Card>
    </React.Fragment>
  );
};

export default UnknownSizeLoadMoreTable;
